import * as React from "react";
import {
  cn,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@racoon/ui";

type ModeTabOption<T extends string> = {
  value: T;
  /** The visible text; with an icon, the button's accessible name and tooltip instead. */
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Shown on hover, also while the option is disabled. */
  tooltip?: string;
};

/**
 * A segmented control: one pressed option at a time.
 *
 * - `track` (default): `@racoon/ui`'s `Tabs` segmented control, like the customer
 *   onboarding's "new / existing" switch — the one look for "pick one option".
 * - `outline`: Figma "Icon button group" — segments joined by 1px dividers (the track's
 *   own fill), the pressed one Surface/selected with a Border/action-subtle outline, like
 *   the route list sidebar's trucks / drivers switch.
 */
export function ModeTabs<T extends string>({
  value,
  options,
  onChange,
  variant = "track",
  testId,
}: {
  value: T;
  options: readonly ModeTabOption<T>[];
  onChange: (value: T) => void;
  variant?: "track" | "outline";
  /** Each button gets `data-testid="<testId>-<value>"`. */
  testId?: string;
}) {
  // A disabled control fires no pointer events, so the wrapper carries the tooltip.
  const withTooltip = (option: ModeTabOption<T>, control: React.ReactNode) =>
    option.tooltip ? (
      <TooltipProvider key={option.value}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-1">{control}</div>
          </TooltipTrigger>
          <TooltipContent>{option.tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      control
    );

  if (variant === "track") {
    return (
      <Tabs
        value={value}
        onValueChange={(next) => onChange(next as T)}
        // Arrows only move focus; Enter/Space or a click picks, as the old buttons did.
        activationMode="manual"
        className="w-full"
      >
        <TabsList className="w-full">
          {options.map((option) =>
            withTooltip(
              option,
              <TabsTrigger
                key={option.value}
                value={option.value}
                data-testid={testId && `${testId}-${option.value}`}
                aria-label={option.icon ? option.label : undefined}
                title={option.icon ? option.label : undefined}
                disabled={option.disabled}
                className="flex-1"
              >
                {option.icon ?? option.label}
              </TabsTrigger>,
            ),
          )}
        </TabsList>
      </Tabs>
    );
  }

  return (
    <div className="flex w-full gap-px rounded-[8px] bg-ds-border-primary p-px">
      {options.map((option, index) => {
        const pressed = value === option.value;
        return withTooltip(
          option,
          <button
            key={option.value}
            type="button"
            data-testid={testId && `${testId}-${option.value}`}
            aria-pressed={pressed}
            aria-label={option.icon ? option.label : undefined}
            title={option.icon ? option.label : undefined}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              // 7px = the track's 8px radius minus its 1px padding.
              "flex h-[30px] flex-1 items-center justify-center p-1 transition-colors disabled:pointer-events-none disabled:opacity-50",
              index === 0 && "rounded-l-[7px]",
              index === options.length - 1 && "rounded-r-[7px]",
              pressed
                ? "relative bg-ds-surface-selected text-ds-icon-action outline outline-1 outline-ds-border-action-subtle"
                : "bg-white text-ds-icon-default hover:bg-ds-surface-secondary",
            )}
          >
            {option.icon ?? option.label}
          </button>,
        );
      })}
    </div>
  );
}
